import { useCallback } from 'react';
import { CffAsyncTypeahead, type CffAsyncOption } from '../forms/CffAsyncTypeahead';
import { acraRegistryFromEntity, searchAcraRegistryOptions } from '../../lib/acraRegistry';
import type { AcraEntityRecord } from '../../lib/dataGovSg';
import type { AccountIntelRegistry } from '../../types/crm';

type AcraUenTypeaheadProps = {
  value: string;
  onChange: (uen: string) => void;
  onRegistryPick?: (registry: AccountIntelRegistry) => void;
  onManualInput?: () => void;
  disabled?: boolean;
};

export function AcraUenTypeahead({
  value,
  onChange,
  onRegistryPick,
  onManualInput,
  disabled = false,
}: AcraUenTypeaheadProps) {
  const search = useCallback(async (query: string): Promise<CffAsyncOption[]> => {
    const rows = await searchAcraRegistryOptions(query, 15);
    return rows.map((row: AcraEntityRecord) => ({
      value: row.uen,
      label: `${row.uen} — ${row.entityName}${row.status ? ` (${row.status})` : ''}`,
      meta: row,
    }));
  }, []);

  const handleSelect = useCallback(
    (option: CffAsyncOption) => {
      const row = option.meta as AcraEntityRecord | undefined;
      if (!row?.uen) return;
      onChange(row.uen);
      onRegistryPick?.(acraRegistryFromEntity(row));
    },
    [onChange, onRegistryPick],
  );

  return (
    <CffAsyncTypeahead
      value={value}
      onChange={(next) => {
        onChange(next);
        onManualInput?.();
      }}
      onSelect={handleSelect}
      search={search}
      disabled={disabled}
      inputClassName="field-input mono"
      placeholder="UEN, company name, or NA"
      ariaLabel="UEN or company name search"
    />
  );
}
